import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiCalendar, 
  FiUser, 
  FiArrowRight,
  FiTag,
  FiTrendingUp,
  FiZap,
  FiShield,
  FiMonitor
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './Blog.css';

const Blog = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/blog', {
      page_title: 'LogCrest Blog - Insights & Updates',
      page_category: 'blog'
    });
  }, []);

  const featuredPost = {
    title: 'The Future of AI-Powered Observability: What to Expect in 2024',
    excerpt: 'Explore the latest trends and innovations in AI-driven observability that are reshaping how organizations monitor and optimize their infrastructure.',
    author: 'Sarah Chen',
    date: 'December 15, 2024',
    category: 'AI & Machine Learning',
    readTime: '8 min read',
    image: '/api/placeholder/800/400'
  };

  const blogPosts = [
    {
      title: '5 Best Practices for Implementing AI Alerting in Production',
      excerpt: 'Learn how to effectively implement AI-powered alerting systems that reduce noise and improve incident response times.',
      author: 'Mike Rodriguez',
      date: 'December 10, 2024',
      category: 'Best Practices',
      readTime: '6 min read'
    },
    {
      title: 'Understanding Zero Trust Architecture in Observability',
      excerpt: 'Discover how zero trust principles can enhance your observability strategy and improve security posture.',
      author: 'Alex Kim',
      date: 'December 5, 2024',
      category: 'Security',
      readTime: '7 min read'
    },
    {
      title: 'Cost Optimization Strategies for Large-Scale Monitoring',
      excerpt: 'Practical tips for reducing observability costs while maintaining comprehensive monitoring coverage.',
      author: 'Emma Thompson',
      date: 'November 28, 2024',
      category: 'Cost Optimization',
      readTime: '5 min read'
    },
    {
      title: 'Building Resilient Microservices with Intelligent Monitoring',
      excerpt: 'How to design and monitor microservices architectures that can handle failures gracefully.',
      author: 'David Park',
      date: 'November 20, 2024',
      category: 'Architecture',
      readTime: '9 min read'
    },
    {
      title: 'The Role of Machine Learning in Incident Response',
      excerpt: 'Explore how ML algorithms are transforming incident detection, classification, and resolution processes.',
      author: 'Lisa Wang',
      date: 'November 15, 2024',
      category: 'AI & Machine Learning',
      readTime: '6 min read'
    },
    {
      title: 'Observability at Scale: Lessons from Enterprise Deployments',
      excerpt: 'Real-world insights from large-scale observability implementations across different industries.',
      author: 'James Wilson',
      date: 'November 8, 2024',
      category: 'Case Studies',
      readTime: '8 min read'
    }
  ];

  const categories = [
    { name: 'AI & Machine Learning', icon: <FiZap />, count: 12 },
    { name: 'Best Practices', icon: <FiTrendingUp />, count: 8 },
    { name: 'Security', icon: <FiShield />, count: 6 },
    { name: 'Architecture', icon: <FiMonitor />, count: 10 },
    { name: 'Case Studies', icon: <FiUser />, count: 5 }
  ];

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            className="blog-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>LogCrest Blog</h1>
            <p className="hero-description">
              Insights, best practices, and the latest trends in AI-powered observability. 
              Stay ahead with expert knowledge from our team and industry leaders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post Section */}
      <section className="featured-section">
        <div className="container">
          <motion.div
            className="featured-post"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="featured-content">
              <div className="featured-meta">
                <span className="featured-badge">Featured</span>
                <span className="post-category">
                  <FiTag />
                  {featuredPost.category}
                </span>
              </div>
              <h2>{featuredPost.title}</h2>
              <p className="featured-excerpt">{featuredPost.excerpt}</p>
              <div className="post-meta">
                <div className="author-info">
                  <FiUser />
                  <span>{featuredPost.author}</span>
                </div>
                <div className="date-info">
                  <FiCalendar />
                  <span>{featuredPost.date}</span>
                </div>
                <span className="read-time">{featuredPost.readTime}</span>
              </div>
              <Link to="/contact" className="btn btn-primary">
                Read Full Article
                <FiArrowRight />
              </Link>
            </div>
            <div className="featured-image">
              <div className="image-placeholder">
                <FiMonitor />
                <span>Featured Article</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="posts-section">
        <div className="container">
          <div className="posts-layout">
            <div className="posts-main">
              <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2>Latest Articles</h2>
              </motion.div>
              
              <div className="posts-grid">
                {blogPosts.map((post, index) => (
                  <motion.article
                    key={index}
                    className="post-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <div className="post-header">
                      <span className="post-category">
                        <FiTag />
                        {post.category}
                      </span>
                      <h3>{post.title}</h3>
                    </div>
                    <p className="post-excerpt">{post.excerpt}</p>
                    <div className="post-meta">
                      <div className="author-info">
                        <FiUser />
                        <span>{post.author}</span>
                      </div>
                      <div className="date-info">
                        <FiCalendar />
                        <span>{post.date}</span>
                      </div>
                      <span className="read-time">{post.readTime}</span>
                    </div>
                    <Link to="/contact" className="read-more">
                      Read More
                      <FiArrowRight />
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="blog-sidebar">
              <motion.div
                className="sidebar-section"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <h3>Categories</h3>
                <div className="categories-list">
                  {categories.map((category, index) => (
                    <div key={index} className="category-item">
                      <div className="category-icon">
                        {category.icon}
                      </div>
                      <span className="category-name">{category.name}</span>
                      <span className="category-count">({category.count})</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="sidebar-section"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <h3>Stay Updated</h3>
                <p>Get the latest insights delivered to your inbox.</p>
                <Link to="/contact" className="btn btn-outline">
                  Subscribe to Newsletter
                </Link>
              </motion.div>
            </aside>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="blog-cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2>Have a story to share?</h2>
            <p>
              We're always looking for guest contributors and industry experts 
              to share their insights on observability and AI.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Write for Us
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
